// The airline brand remembered on this device for the staff sign-in pages.
//
// It holds only what GET /v1/public/lookup/:code returns publicly (name, logo, icon, colours,
// code), so it carries no security meaning: it only decides how the sign-in page looks the next
// time — after a session expiry, a sign-out, or when the installed app opens.

export const LOGIN_BRAND_KEY = 'login_brand'

const CODE_RE = /^[a-z0-9]{2,10}$/i
const BRAND_FIELDS = ['code', 'name', 'logo', 'icon', 'color1', 'color2']

/** Airline code from a URL segment → lowercase, or null when it can't be one. */
export function normaliseCode(raw) {
  if (typeof raw !== 'string') return null
  const code = raw.trim()
  return CODE_RE.test(code) ? code.toLowerCase() : null
}

/** Only the public, cosmetic fields of a company, with a normalised code. */
export function publicBrandFrom(company) {
  if (!company) return null
  const code = normaliseCode(company.code)
  if (!code) return null
  const brand = { code }
  BRAND_FIELDS.slice(1).forEach((field) => {
    if (company[field]) brand[field] = company[field]
  })
  return brand
}

export function loadLoginBrand(storage = window.localStorage) {
  try {
    const brand = JSON.parse(storage.getItem(LOGIN_BRAND_KEY) || 'null')
    return brand && normaliseCode(brand.code) ? brand : null
  } catch (e) {
    return null
  }
}

export function saveLoginBrand(brand, storage = window.localStorage) {
  const clean = publicBrandFrom(brand)
  if (!clean) return
  try {
    storage.setItem(LOGIN_BRAND_KEY, JSON.stringify({ ...clean, savedAt: Date.now() }))
  } catch (e) {
    // private mode / storage full — branding just won't be remembered
  }
}

export function clearLoginBrand(storage = window.localStorage) {
  try {
    storage.removeItem(LOGIN_BRAND_KEY)
  } catch (e) {
    // ignore
  }
}

/** After a sign-in: airline users → remember their airline; Vamos staff/developers → forget. */
export function rememberBrandForUser(user, storage = window.localStorage) {
  if (user?.type === 'company' && publicBrandFrom(user.company)) {
    saveLoginBrand(user.company, storage)
  } else {
    clearLoginBrand(storage)
  }
}

/**
 * A post-sign-in destination is accepted only as a same-origin relative path: it must start
 * with a single '/', and stay short. Anything else (absolute URLs, '//host', 'javascript:')
 * is dropped — this is an open-redirect guard.
 */
export function safeNext(raw) {
  if (typeof raw !== 'string') return null
  const next = raw.trim()
  if (!next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return null
  if (next.length > 512) return null
  // Never bounce back into the auth pages themselves.
  if (
    /^\/(login|logout|mfa-verify|forgot-password|reset-password|change|sso|sso-callback|verify-email)(\/|$|\?)/.test(
      next,
    )
  ) {
    return null
  }
  return next
}

/** Current app location as a `next` value, or null when it isn't worth returning to. */
export function currentNext(location = window.location) {
  const path = `${location.pathname || '/'}${location.search || ''}`
  return path === '/' ? null : safeNext(path)
}

/** The airline code of a stored session's user (airline staff), or null (Vamos staff, none). */
export function brandCodeForAuth(auth) {
  return auth?.user?.type === 'company' ? normaliseCode(auth.user.company?.code) : null
}

/** The signed-in session stored on this device, or null. */
export function readStoredAuth(storage = window.localStorage) {
  try {
    return JSON.parse(storage.getItem('auth') || 'null')
  } catch (e) {
    return null
  }
}

/**
 * Sign-in URL for an AUTOMATIC return to sign-in (sign-out, session expired, idle timeout): the
 * sign-in page of the airline of the user who was signed in (/login/<code>), or /login for Vamos
 * staff. It comes from the session itself — never from an airline seen earlier on the device —
 * so call it BEFORE the stored session is cleared.
 */
export function sessionLoginPath(params = {}, auth = readStoredAuth(), pinned = readPinnedBrand()) {
  // In an installed airline app the window's airline wins over the user's.
  return loginPath(pinned || brandCodeForAuth(auth), params)
}

// Same key as pinnedBrand.js (kept here to avoid a circular import).
function readPinnedBrand() {
  try {
    return normaliseCode(window.sessionStorage.getItem('pinned_brand')) || null
  } catch (e) {
    return null
  }
}

/** '/login' + code + query string. `params` values that are empty are skipped. */
export function loginPath(code, params = {}) {
  const base = code ? `/login/${code}` : '/login'
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value)
  })
  const qs = query.toString()
  return qs ? `${base}?${qs}` : base
}
